"use client";

import { isLiveSwiper } from '@/lib/swiper-lifecycle';
import React, { useState, useEffect, useRef, useId } from 'react';
import { motion } from "framer-motion";
import { Navigation, Autoplay, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import Image from '@/components/ui/MediaImage';
import type { PageUrlMap } from "@/types/blog";
import { resolveUrlFromMap, buildLocalizedLink } from "@/lib/blog-transformers";
import { UrlBuilder } from '@/lib/url-builder';
import { Fancybox } from "@fancyapps/ui/dist/fancybox/";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import { transformMediaUrl } from '@/lib/media-url-transformer';
import { appendMediaProxySize } from '@/lib/media-proxy-size';
import { shouldEmbedBlockPropsInDom } from '@/lib/embed-block-props-dom';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

// Interface tanımları
export interface Slide {
    id: number;
    imageUrl: string;
    imageAlt: string;
    thumbImageUrl?: string; // Thumbnail için ayrı path (opsiyonel)
    /** Lightbox'ta açılacak tam boyut URL (yoksa imageUrl kullanılır) */
    fullSizeImageUrl?: string;
    title?: string;
    description?: string;
    highlightWord?: string;
    link?: {
        type: 'page' | 'product' | 'category' | 'custom';
        value: string;
        label: string;
        slug?: string;
        templateKey?: string;
    };
}

function resolveGallerySlideLink(
    link: Slide['link'],
    pageUrls?: PageUrlMap,
    language?: string,
    defaultLanguage = 'en',
): string | null {
    if (!link) return null;
    const rawValue = String(link.value || link.slug || '').trim();

    if (link.type === 'custom' && rawValue) {
        return rawValue;
    }

    if (link.type === 'page' && rawValue) {
        if (pageUrls && language) {
            const baseUrl = resolveUrlFromMap(
                pageUrls,
                rawValue,
                language,
                defaultLanguage,
                `/${rawValue}`,
            );
            return buildLocalizedLink(baseUrl, rawValue, defaultLanguage);
        }
        return `/${rawValue}`;
    }

    if (!pageUrls || !language) {
        return null;
    }

    const builder = new UrlBuilder(pageUrls, language, defaultLanguage);

    if (link.type === 'product' && rawValue) {
        const productType = link.templateKey?.replace('-detail', '') || 'tour';
        return builder.buildProductDetailUrl(productType, rawValue);
    }

    if (link.type === 'category' && rawValue) {
        const productType = link.templateKey?.replace('-cat', '') || 'tour';
        return builder.buildCategoryUrl(productType, rawValue);
    }

    return null;
}

interface ImageGallerySliderProps {
    slides?: Slide[];
    pageUrls?: PageUrlMap;
    language?: string;
    defaultLanguage?: string;
    /** true ise sadece thumbnail satırı gösterilir (büyük ana alan gizlenir), tıklanınca Fancybox ile açılır */
    thumbnailsOnly?: boolean;
    /**
     * Slides boşken demo (Unsplash) gösterilsin mi — yalnızca GrapesJS önizleme için true kalmalı.
     * Ürün sayfası gibi gerçek içerikte false verin; aksi halde boş galeri demo görselleri gösterir.
     */
    showDemoWhenEmpty?: boolean;
}

// Demo slides for GrapesJS preview (when no slides provided)
const DEMO_SLIDES: Slide[] = [
    {
        id: 1,
        imageUrl: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1000&h=550&fit=crop',
        imageAlt: 'Mountain Landscape',
    },
    {
        id: 2,
        imageUrl: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1000&h=550&fit=crop',
        imageAlt: 'Forest Nature',
    },
    {
        id: 3,
        imageUrl: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1000&h=550&fit=crop',
        imageAlt: 'Green Forest',
    },
    {
        id: 4,
        imageUrl: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=1000&h=550&fit=crop',
        imageAlt: 'Mountain View',
    },
    {
        id: 5,
        imageUrl: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1000&h=550&fit=crop',
        imageAlt: 'Lake Scenery',
    },
    {
        id: 6,
        imageUrl: 'https://images.unsplash.com/photo-1511497584788-876760111969?w=1000&h=550&fit=crop',
        imageAlt: 'Desert Landscape',
    },
    {
        id: 7,
        imageUrl: 'https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=1000&h=550&fit=crop',
        imageAlt: 'Ocean View',
    },
    {
        id: 8,
        imageUrl: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1000&h=550&fit=crop',
        imageAlt: 'Mountain Peak',
    },
    {
        id: 9,
        imageUrl: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1000&h=550&fit=crop',
        imageAlt: 'Valley View',
    },
    {
        id: 10,
        imageUrl: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1000&h=550&fit=crop',
        imageAlt: 'Sunset Landscape',
    },
];

export default function ImageGallerySlider({ 
    slides = [],
    pageUrls,
    language,
    defaultLanguage = 'en',
    thumbnailsOnly = false,
    showDemoWhenEmpty = true,
}: ImageGallerySliderProps) {
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
    const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null);
    const [componentProps, setComponentProps] = useState<{ slides?: Slide[]; product_slug?: string }>({});
    const galleryId = useId();
    const galleryIdRef = useRef<string>(`gallery-${galleryId.replace(/:/g, '-')}`);
    const containerRef = useRef<HTMLDivElement | null>(null);
    // Component hydration: data-component-props'tan props'ları al
    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return;
        const updateProps = () => {
            // Eğer component bir DOM element'i içindeyse (GrapesJS hydration)
            const element = document.querySelector('[data-component-path="sections/ImageGallerySlider"]');
            if (element) {
                const propsAttr = element.getAttribute('data-component-props');
                if (propsAttr) {
                    try {
                        const props = JSON.parse(propsAttr);
       
                        setComponentProps(props);
                        if (props.slides && Array.isArray(props.slides)) {
                            // Props'tan gelen slides'ı kullan
                            return;
                        }
                    } catch {
                        // Props parse hatası
                    }
                }
            }
        };

        // İlk yükleme
        updateProps();

        // MutationObserver ile props değişikliklerini dinle
        const observer = new MutationObserver(updateProps);
        const element = document.querySelector('[data-component-path="sections/ImageGallerySlider"]');
        if (element) {
            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-component-props']
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    // Props'tan veya componentProps'tan slides'ı al
    // GrapesJS vb. için slides boşken demo kullanılabilir; ürün sayfasında showDemoWhenEmpty={false}
    const allSlides =
        componentProps.slides && componentProps.slides.length > 0
            ? componentProps.slides
            : slides.length > 0
              ? slides
              : showDemoWhenEmpty
                ? DEMO_SLIDES
                : [];
    
    // Geçerli slide'ları al; aynı resim (URL bazlı) iki kez listelenmesin
    const seenNormalized = new Set<string>();
    const slidesToRender = allSlides.filter((slide) => {
        if (!slide?.imageUrl || typeof slide.imageUrl !== 'string') {
            return false;
        }
        const url = slide.imageUrl.trim();
        if (!url.startsWith('/') && !url.startsWith('http://') && !url.startsWith('https://')) {
            return false;
        }
        const normalized = url.split('?')[0];
        if (seenNormalized.has(normalized)) {
            return false;
        }
        seenNormalized.add(normalized);
        return true;
    });

    const resolveMediaUrl = (value?: string): string => {
        if (!value || typeof value !== 'string') return '';
        return transformMediaUrl(value, null) || value;
    };

    const displayMediaUrl = (value: string, size: 'medium' | 'large' | 'xlarge' = 'large') =>
        appendMediaProxySize(resolveMediaUrl(value), size);

    const lightboxMediaUrl = (slide: (typeof slidesToRender)[0]) => {
        const raw = slide.fullSizeImageUrl || slide.imageUrl;
        return appendMediaProxySize(resolveMediaUrl(raw), 'xlarge');
    };

    useEffect(() => {
        if (typeof window === 'undefined' || slidesToRender.length === 0 || !containerRef.current) return;

        const container = containerRef.current;
        const gallerySelector = `[data-fancybox="${galleryIdRef.current}"]`;

        Fancybox.bind(container, gallerySelector, {
            Image: {
                zoom: true,
                wheel: "slide",
            },
        } as any);

        const handleReveal = () => {
            document.body.style.overflow = 'hidden';
        };

        const handleDestroy = () => {
            document.body.style.overflow = '';
        };

        container.addEventListener('fancybox:reveal', handleReveal);
        container.addEventListener('fancybox:destroy', handleDestroy);

        return () => {
            container.removeEventListener('fancybox:reveal', handleReveal);
            container.removeEventListener('fancybox:destroy', handleDestroy);
            Fancybox.unbind(container);
            Fancybox.close();
        };
    }, [slidesToRender.length]);

    const getLightboxUrl = (slide: typeof slidesToRender[0]) => lightboxMediaUrl(slide);

    if (slidesToRender.length === 0) {
        return null;
    }

    return (
        <motion.div 
            ref={containerRef}
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
            initial="hidden"
            animate="visible"
            className="relative"
        >
            {!thumbnailsOnly && (
            <div className="relative mb-8">
                <Swiper 
                    modules={[Navigation, Thumbs, Autoplay]} 
                    thumbs={{ swiper: isLiveSwiper(thumbsSwiper) ? thumbsSwiper : null }}
                    navigation={{
                        nextEl: '.swiper-button-next-custom',
                        prevEl: '.swiper-button-prev-custom',
                    }}
                    onSwiper={setMainSwiper}
                    onBeforeDestroy={(instance) => setMainSwiper(current => current === instance ? null : current)}
                    className="image-gallery-swiper rounded-lg overflow-hidden"
                    loop={true}
                    spaceBetween={30}
                    autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                    }}
                    breakpoints={{
                        320: {
                            slidesPerView: 1,
                        },
                        640: {
                            slidesPerView: 2,
                        },
                        768: {
                            slidesPerView: 3,
                        },
                    }}
                >
                    {slidesToRender.map((slide, index) => {
                        const lightboxUrl = getLightboxUrl(slide);
                        const mainImageUrl = displayMediaUrl(slide.imageUrl, index === 0 ? 'large' : 'medium');
                        const resolvedThumbUrl = slide.thumbImageUrl
                            ? displayMediaUrl(slide.thumbImageUrl, 'medium')
                            : displayMediaUrl(slide.imageUrl, 'medium');
                        const slideHref = resolveGallerySlideLink(
                            slide.link,
                            pageUrls,
                            language,
                            defaultLanguage,
                        );
                        
                        return (
                        <SwiperSlide key={slide.id} className="relative h-full overflow-hidden flex items-stretch group">
                            {slideHref ? (
                                <a 
                                    href={slideHref}
                                    className="block w-full h-full relative"
                                >
                                    <a
                                        href={lightboxUrl}
                                        data-fancybox={galleryIdRef.current}
                                        data-caption={slide.imageAlt}
                                        className="block relative w-full h-full cursor-pointer"
                                    >
                                        <Image 
                                            src={mainImageUrl} 
                                            alt={slide.imageAlt} 
                                            fill
                                            className="object-cover transition-transform duration-300 group-hover:scale-105"
                                            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                                            loading={index === 0 ? undefined : 'lazy'}
                                            priority={index === 0}
                                            unoptimized={mainImageUrl.startsWith('/api/media/proxy/') || mainImageUrl.includes('unsplash.com')}
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                                                <svg className="w-6 h-6 text-alsero-first" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </a>
                                </a>
                            ) : (
                                <a
                                    href={lightboxUrl}
                                    data-fancybox={galleryIdRef.current}
                                    data-caption={slide.imageAlt}
                                    className="block relative w-full h-full cursor-pointer"
                                >
                                    <Image 
                                        src={mainImageUrl} 
                                        alt={slide.imageAlt} 
                                        fill
                                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                                        loading={index === 0 ? undefined : 'lazy'}
                                        priority={index === 0}
                                        unoptimized={mainImageUrl.startsWith('/api/media/proxy/') || mainImageUrl.includes('unsplash.com')}
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                                            <svg className="w-6 h-6 text-alsero-first" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                                            </svg>
                                        </div>
                                    </div>
                                </a>
                            )}
                        </SwiperSlide>
                        );
                    })}
                </Swiper>
                
                {/* Navigation Buttons */}
                <div className="swiper-button-prev-custom absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full w-12 h-12 flex items-center justify-center cursor-pointer transition-all duration-300">
                    <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </div>
                <div className="swiper-button-next-custom absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full w-12 h-12 flex items-center justify-center cursor-pointer transition-all duration-300">
                    <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>
            )}
            <Swiper
                modules={[Thumbs]}
                watchSlidesProgress={!thumbnailsOnly}
                onSwiper={setThumbsSwiper}
                onBeforeDestroy={(instance) => setThumbsSwiper(current => current === instance ? null : current)}
                freeMode={true}
                spaceBetween={thumbnailsOnly ? 16 : 20}
                breakpoints={thumbnailsOnly ? {
                    320: { slidesPerView: 2.5 },
                    480: { slidesPerView: 3.5 },
                    640: { slidesPerView: 4 },
                    768: { slidesPerView: 5 },
                    1024: { slidesPerView: 6 },
                } : {
                    320: { slidesPerView: 4 },
                    640: { slidesPerView: 6 },
                    768: { slidesPerView: 9.5 },
                    1024: { slidesPerView: 12.5 },
                }}
                className={`thumbnail-swiper ${thumbnailsOnly ? 'thumbnail-swiper-only' : ''}`}
            >
                    {slidesToRender.map((slide, index) => {
                        const lightboxUrl = getLightboxUrl(slide);
                        const resolvedThumbUrl = slide.thumbImageUrl
                            ? displayMediaUrl(slide.thumbImageUrl, 'medium')
                            : displayMediaUrl(slide.imageUrl, 'medium');
                        return (
                    <SwiperSlide 
                        key={`thumb-${slide.id}`} 
                        className={`relative cursor-pointer overflow-hidden border-2 border-transparent hover:border-alsero-first transition-all duration-300 flex items-stretch ${thumbnailsOnly ? 'h-[180px] min-w-[240px] max-w-[240px] rounded-lg shadow-md' : 'h-[110px] min-w-[130px] max-w-[130px]'}`}
                    >
                        <a
                                href={lightboxUrl}
                            data-fancybox={galleryIdRef.current}
                            data-caption={slide.imageAlt}
                            className="block relative w-full h-full opacity-100"
                        >
                            <Image
                                    src={resolvedThumbUrl}
                                alt={slide.imageAlt}
                                width={thumbnailsOnly ? 240 : 130}
                                height={thumbnailsOnly ? 180 : 110}
                                className="object-cover w-full h-full opacity-100 saturate-100"
                                loading="lazy"
                                    unoptimized={resolvedThumbUrl.startsWith('/api/media/proxy/') || resolvedThumbUrl.includes('unsplash.com')}
                            />
                        </a>
                    </SwiperSlide>
                        );
                    })}
            </Swiper>
        </motion.div>
    );
}

